import React, { useEffect, useRef, useState } from "react";
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import { PROFILE_TYPE_CATALOG } from "./profileTypeCatalog.js";

const PROFILE_TYPES_URL = "/umbraco/api/WatchlistsApi/WatchlistProfileTypes";
const WATCHLIST_ITEMS_URL = "/umbraco/api/WatchlistsApi/WatchlistItems";
const PAGE_SIZE = 10;

let searchDebounce = null;

const emptyTypeState = () => ({
    items: [],
    page: 0,
    hasMore: false,
    isLoadingMore: false,
    total: 0,
});

const readErrorMessage = async (response, fallback) => {
    const message = await response.text();
    return message || fallback;
};

const resultKey = (profileType, result) => {
    const uniqueId = result?.UniqueId || result?.uniqueId || "";
    return `${profileType}::${uniqueId}`;
};

const mergeItemsByUniqueId = (current = [], incoming = []) => {
    const map = new Map();
    [...current, ...incoming].forEach((item) => {
        if (!item?.UniqueId)
            return;
        map.set(String(item.UniqueId), {
            UniqueId: String(item.UniqueId),
            Name: item.Name || String(item.UniqueId),
        });
    });
    return Array.from(map.values());
};

const idsFromItems = (itemsByType) => {
    const next = {};
    Object.keys(itemsByType || {}).forEach((type) => {
        next[type] = (itemsByType[type] || []).map((item) => String(item.UniqueId));
    });
    return next;
};

const fetchTypePage = async (config, phrase, page) => {
    const url = `/umbraco/api/WatchlistsApi/SearchProfiles?profileType=${encodeURIComponent(config.type)}&phrase=${encodeURIComponent(phrase)}&page=${page}&pageSize=${PAGE_SIZE}`;
    const response = await fetch(url);
    if (!response.ok)
        return { type: config.type, results: [], hasMore: false, page, total: 0 };

    const data = await response.json();
    const results = Array.isArray(data)
        ? data
        : (Array.isArray(data?.Results)
            ? data.Results
            : (Array.isArray(data?.results) ? data.results : []));

    const rawTotal = Array.isArray(data) ? null : (data?.Total ?? data?.total);
    const parsedTotal = rawTotal == null || rawTotal === "" ? NaN : Number(rawTotal);
    const total = Number.isFinite(parsedTotal) ? parsedTotal : results.length;
    const loadedThroughPage = (page - 1) * PAGE_SIZE + results.length;
    const hasMore = Number.isFinite(parsedTotal)
        ? loadedThroughPage < total
        : results.length >= PAGE_SIZE;

    return {
        type: config.type,
        results,
        hasMore,
        page,
        total,
    };
};

const AddProfilesPopup = ({
    isOpen,
    onClose,
    workspaceId,
    enabledProfileTypes = [],
    onProfilesChanged,
}) => {
    const [searchPhrase, setSearchPhrase] = useState("");
    const [resultsByType, setResultsByType] = useState({});
    const [isSearching, setIsSearching] = useState(false);
    const [addedItemsByType, setAddedItemsByType] = useState({});
    const [addingKeys, setAddingKeys] = useState({});
    const [errorMessage, setErrorMessage] = useState(null);
    const searchRequestId = useRef(0);
    const enabledTypesRef = useRef(enabledProfileTypes || []);
    const addedIdsRef = useRef({});
    const phraseRef = useRef("");
    const resultsByTypeRef = useRef({});
    const loadingMoreRef = useRef({});

    useEffect(() => {
        resultsByTypeRef.current = resultsByType;
    }, [resultsByType]);

    useEffect(() => {
        enabledTypesRef.current = enabledProfileTypes || [];
    }, [enabledProfileTypes]);

    useEffect(() => {
        addedIdsRef.current = idsFromItems(addedItemsByType);
    }, [addedItemsByType]);

    useEffect(() => {
        if (!isOpen || !workspaceId)
            return;

        setSearchPhrase("");
        phraseRef.current = "";
        setResultsByType({});
        setIsSearching(false);
        setAddingKeys({});
        setErrorMessage(null);
        loadingMoreRef.current = {};
        searchRequestId.current += 1;

        // Preload existing watchlist items so "Added" reflects real membership.
        // Do not rerun when a profile type is added; that refresh must leave the search and result lists in place.
        const enabledTypes = enabledTypesRef.current || [];
        const typesToLoad = enabledTypes.length > 0
            ? PROFILE_TYPE_CATALOG.filter((c) =>
                enabledTypes.some((t) => String(t).toLowerCase() === String(c.type).toLowerCase())
            )
            : PROFILE_TYPE_CATALOG;

        Promise.all(
            typesToLoad.map((config) =>
                fetch(`${WATCHLIST_ITEMS_URL}?workspaceId=${workspaceId}&profileType=${encodeURIComponent(config.type)}`)
                    .then(async (response) => {
                        if (!response.ok)
                            return { type: config.type, items: [] };
                        const data = await response.json();
                        const items = (Array.isArray(data) ? data : []).map((item) => ({
                            UniqueId: String(item.UniqueId || item.uniqueId || ""),
                            Name: item.Name || String(item.UniqueId || ""),
                        })).filter((item) => item.UniqueId);
                        return { type: config.type, items };
                    })
                    .catch(() => ({ type: config.type, items: [] }))
            )
        ).then((batches) => {
            const next = {};
            batches.forEach(({ type, items }) => {
                if (items.length)
                    next[type] = items;
            });
            setAddedItemsByType(next);
            addedIdsRef.current = idsFromItems(next);
        });
    }, [isOpen, workspaceId]);

    useEffect(() => {
        return () => {
            if (searchDebounce)
                clearTimeout(searchDebounce);
        };
    }, []);

    const isAlreadyOnWatchlist = (profileType, uniqueId) => {
        if (!uniqueId)
            return false;
        const id = String(uniqueId);
        const existing = addedIdsRef.current[profileType] || [];
        return existing.some((item) => String(item) === id);
    };

    const runSearch = (phrase) => {
        const trimmed = (phrase || "").trim();
        phraseRef.current = trimmed;
        if (searchDebounce)
            clearTimeout(searchDebounce);

        if (trimmed.length < 2) {
            searchRequestId.current += 1;
            setIsSearching(false);
            setResultsByType({});
            return;
        }

        searchDebounce = setTimeout(() => {
            const requestId = ++searchRequestId.current;
            setIsSearching(true);
            setErrorMessage(null);
            loadingMoreRef.current = {};

            Promise.all(
                PROFILE_TYPE_CATALOG.map((config) =>
                    fetchTypePage(config, trimmed, 1).catch(() => ({
                        type: config.type,
                        results: [],
                        hasMore: false,
                        page: 1,
                        total: 0,
                    }))
                )
            )
                .then((batches) => {
                    if (requestId !== searchRequestId.current)
                        return;

                    const nextResults = {};
                    batches.forEach(({ type, results, hasMore, page, total }) => {
                        nextResults[type] = {
                            items: results,
                            page,
                            hasMore,
                            isLoadingMore: false,
                            total: total || 0,
                        };
                    });
                    setResultsByType(nextResults);
                })
                .finally(() => {
                    if (requestId === searchRequestId.current)
                        setIsSearching(false);
                });
        }, 400);
    };

    const loadMoreForType = (profileType) => {
        const phrase = phraseRef.current;
        const state = resultsByTypeRef.current[profileType];
        if (!phrase || phrase.length < 2 || !state?.hasMore || state.isLoadingMore || loadingMoreRef.current[profileType])
            return;

        const config = PROFILE_TYPE_CATALOG.find((item) => item.type === profileType);
        if (!config)
            return;

        const nextPage = (state.page || 1) + 1;
        const requestId = searchRequestId.current;
        loadingMoreRef.current[profileType] = true;

        setResultsByType((prev) => ({
            ...prev,
            [profileType]: {
                ...(prev[profileType] || emptyTypeState()),
                isLoadingMore: true,
            },
        }));

        fetchTypePage(config, phrase, nextPage)
            .then(({ results, hasMore, page, total }) => {
                if (requestId !== searchRequestId.current || phraseRef.current !== phrase)
                    return;

                setResultsByType((prev) => {
                    const current = prev[profileType] || emptyTypeState();
                    const existingIds = new Set(
                        (current.items || []).map((item) => item?.UniqueId || item?.uniqueId)
                    );
                    const merged = [
                        ...(current.items || []),
                        ...results.filter((item) => {
                            const id = item?.UniqueId || item?.uniqueId;
                            return id && !existingIds.has(id);
                        }),
                    ];
                    return {
                        ...prev,
                        [profileType]: {
                            items: merged,
                            page,
                            hasMore,
                            isLoadingMore: false,
                            total: total > 0 ? total : (current.total || 0),
                        },
                    };
                });
            })
            .catch(() => {
                setResultsByType((prev) => ({
                    ...prev,
                    [profileType]: {
                        ...(prev[profileType] || emptyTypeState()),
                        isLoadingMore: false,
                        hasMore: false,
                    },
                }));
            })
            .finally(() => {
                loadingMoreRef.current[profileType] = false;
            });
    };

    const onResultsScroll = (profileType, event) => {
        const el = event.currentTarget;
        if (!el)
            return;
        const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
        if (remaining < 40)
            loadMoreForType(profileType);
    };

    const onSearchChange = (value) => {
        setSearchPhrase(value);
        runSearch(value);
    };

    const clearSearch = () => {
        setSearchPhrase("");
        runSearch("");
    };

    const ensureProfileTypeEnabled = async (profileType) => {
        const current = enabledTypesRef.current || [];
        if (current.some((t) => String(t).toLowerCase() === String(profileType).toLowerCase()))
            return false;

        const formData = new FormData();
        formData.append("workspaceId", workspaceId);
        formData.append("profileType", profileType);

        const response = await fetch(PROFILE_TYPES_URL, { method: "POST", body: formData });
        if (!response.ok)
            throw new Error(await readErrorMessage(response, "Could not enable profile type"));

        const types = await response.json();
        enabledTypesRef.current = Array.isArray(types) ? types : [...current, profileType];
        return true;
    };

    const addResult = async (profileType, result) => {
        const uniqueId = result?.UniqueId || result?.uniqueId;
        const slug = result?.Slug || result?.slug || uniqueId;
        const key = resultKey(profileType, result);

        if (!uniqueId || addingKeys[key] || isAlreadyOnWatchlist(profileType, uniqueId))
            return;

        setErrorMessage(null);
        setAddingKeys((prev) => ({ ...prev, [key]: true }));

        try {
            const typeWasCreated = await ensureProfileTypeEnabled(profileType);

            const formData = new FormData();
            formData.append("type", profileType);
            formData.append("uniqueId", uniqueId);
            formData.append("slug", slug);
            formData.append("workspaceId", workspaceId);

            const response = await fetch(WATCHLIST_ITEMS_URL, { method: "POST", body: formData });
            if (!response.ok)
                throw new Error(await readErrorMessage(response, "Could not add profile"));

            const id = String(uniqueId);
            const name = result.Name || id;
            setAddedItemsByType((prev) => {
                const next = {
                    ...prev,
                    [profileType]: mergeItemsByUniqueId(prev[profileType], [{ UniqueId: id, Name: name }]),
                };
                addedIdsRef.current = idsFromItems(next);
                return next;
            });
            onProfilesChanged?.({ profileType, typeWasCreated });
        } catch (err) {
            setErrorMessage(err.message || "Could not add profile");
        } finally {
            setAddingKeys((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        }
    };

    if (!isOpen)
        return null;

    const hasSearched = (searchPhrase || "").trim().length >= 2;

    return (
        <div
            className="watchlists-add-profiles-overlay"
            onClick={(e) => {
                if (e.target === e.currentTarget)
                    onClose?.();
            }}
        >
            <div className="add-task-popup rims-filter-container watchlists-add-profiles-popup">
                <div className="rims-filter-header">
                    <h3>Add Profiles</h3>
                    <button
                        type="button"
                        className="close-button"
                        onClick={onClose}
                        style={{ border: "none", background: "transparent", cursor: "pointer" }}
                        aria-label="Close"
                    >
                        <i className="fas fa-times" />
                    </button>
                </div>

                <div className="rims-filter-input-wrapper" style={{ width: "100%", marginBottom: "18px" }}>
                    <input
                        className="rims-filter-input"
                        type="text"
                        autoFocus
                        value={searchPhrase}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder="Search all profile types..."
                        style={{ width: "100%" }}
                    />
                    {searchPhrase !== "" && (
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                clearSearch();
                            }}
                            className="rims-filter-clear-search"
                        >
                            ×
                        </div>
                    )}
                </div>

                {isSearching && (
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: "12px" }}>
                        <LoadingSpinnerImage />
                    </div>
                )}

                {errorMessage && (
                    <div style={{ color: "#e53e3e", marginBottom: "12px", fontSize: "13px" }}>{errorMessage}</div>
                )}

                {!hasSearched && !isSearching && (
                    <div className="watchlists-add-profiles-banner watchlists-add-profiles-banner-hint">
                        <i className="fas fa-search" aria-hidden="true" />
                        <span>Type at least 2 characters to search and add profiles.</span>
                    </div>
                )}

                {hasSearched && !isSearching && PROFILE_TYPE_CATALOG.filter((config) => {
                    const typeState = resultsByType[config.type] || emptyTypeState();
                    return (typeState.items || []).length > 0;
                }).map((config) => {
                    const type = config.type;
                    const typeState = resultsByType[type] || emptyTypeState();
                    const results = typeState.items || [];
                    const resultCountLabel = String(typeState.total || results.length);

                    return (
                        <div className="rims-filter-block" key={type}>
                            <div className="rims-filter-block-header" style={{ cursor: "default" }}>
                                <label className="rims-filter-label">
                                    {config.label}
                                    <span className="watchlists-add-profiles-type-count"> ({resultCountLabel})</span>
                                </label>
                            </div>

                            <div
                                className="rims-filter-dropdown watchlists-add-profiles-dropdown"
                                onScroll={(e) => onResultsScroll(type, e)}
                            >
                                {results.map((result, index) => {
                                    const uniqueId = result?.UniqueId || result?.uniqueId;
                                    const key = resultKey(type, result);
                                    const alreadyAdded = isAlreadyOnWatchlist(type, uniqueId);
                                    const isAdding = !!addingKeys[key];

                                    return (
                                        <div key={`${key}-${index}`} className="rims-filter-dropdown-item watchlists-add-profiles-result">
                                            <span className="watchlists-add-profiles-result-name">
                                                {result.Name || uniqueId || "Untitled"}
                                            </span>
                                            <button
                                                type="button"
                                                className="watchlists-add-profiles-add-btn"
                                                disabled={!uniqueId || alreadyAdded || isAdding}
                                                onClick={() => addResult(type, result)}
                                            >
                                                {isAdding ? (
                                                    <LoadingSpinnerImage small={true} />
                                                ) : alreadyAdded ? "Added" : "Add"}
                                            </button>
                                        </div>
                                    );
                                })}
                                {typeState.isLoadingMore && (
                                    <div className="watchlists-add-profiles-loading-more">
                                        <LoadingSpinnerImage />
                                    </div>
                                )}
                                {!typeState.isLoadingMore && typeState.hasMore && (
                                    <div className="watchlists-add-profiles-scroll-hint">
                                        Scroll for more
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}

                {hasSearched && !isSearching && Object.keys(resultsByType).length > 0 &&
                    PROFILE_TYPE_CATALOG.every((config) => ((resultsByType[config.type] || emptyTypeState()).items || []).length === 0) && (
                    <div className="watchlists-add-profiles-banner watchlists-add-profiles-banner-hint">
                        <span>No profiles match this search.</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddProfilesPopup;
